import { z } from "zod";

export const ConsentCategoriesSchema = z.object({
  essential: z.literal(true),
  functional: z.boolean(),
  analytics: z.boolean(),
});

export const ConsentStateSchema = z.object({
  version: z.number(),
  timestamp: z.string(),
  categories: ConsentCategoriesSchema,
});

export type ConsentCategories = z.infer<typeof ConsentCategoriesSchema>;
export type ConsentState = z.infer<typeof ConsentStateSchema>;

export interface CookieConsentContextValue {
  consent: ConsentState | null;
  isConsentGiven: boolean;
  isBannerVisible: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (categories: ConsentCategories) => void;
  resetConsent: () => void;
}

export const CONSENT_COOKIE_NAME = "cookie_consent";
export const CONSENT_VERSION = 1;
export const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

export function parseConsentFromCookie(
  cookieValue: string | undefined,
): ConsentState | null {
  if (!cookieValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(cookieValue);
    const result = ConsentStateSchema.safeParse(parsed);

    if (result.success) {
      return result.data;
    }

    console.warn("[CookieConsent] Invalid cookie format:", result.error.issues);
    return null;
  } catch {
    return null;
  }
}

export function setConsentCookie(consent: ConsentState): void {
  document.cookie = `${CONSENT_COOKIE_NAME}=${JSON.stringify(consent)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function createConsent(categories: ConsentCategories): ConsentState {
  return {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories,
  };
}

export function isConsentValid(consent: ConsentState | null): boolean {
  return consent !== null && consent.version >= CONSENT_VERSION;
}
